/**
 * Hook for async server-side CSV report export.
 *
 * Manages the full lifecycle: start job → poll status → trigger download.
 * Used for large projects where client-side CSV generation is not feasible
 * (steps are not included in the lightweight report payload).
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '@/services/reports';
import { toast } from '@/lib/toast';

interface StartExportOptions {
  /** Optional filter payload to scope the export. */
  filters?: {
    domain?: string[];
    area?: string[];
    status?: string[];
    tester?: string[];
  };
  /** When set, drives the downloaded filename: `report-{projectNumber}-{YYYY-MM-DD}.csv`. */
  projectNumber?: string;
}

function sanitizeSegment(value: string): string {
  return value.replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
}

function buildExportFilename(projectNumber?: string): string {
  const datePart = new Date().toISOString().slice(0, 10);
  const prefix = projectNumber ? sanitizeSegment(projectNumber) : 'project';
  return `report-${prefix}-${datePart}.csv`;
}

export function useReportExport() {
  const [jobId, setJobId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const toastIdRef = useRef<string | number | undefined>();
  const lastProgressToastPct = useRef<number>(-1);
  const optionsRef = useRef<StartExportOptions>({});

  // Poll for status while exporting
  const { data: exportStatus } = useQuery({
    queryKey: ['report-export', jobId],
    queryFn: () => reportsApi.getCsvExportStatus(jobId!),
    enabled: !!jobId && isExporting,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'processing' ? 2000 : false;
    },
  });

  // Handle status changes
  useEffect(() => {
    if (!exportStatus || !isExporting) return;

    if (exportStatus.status === 'completed' && exportStatus.download_url) {
      const a = document.createElement('a');
      a.href = exportStatus.download_url;
      // Suggest a meaningful filename so the browser doesn't fall back to
      // the S3 key basename. Same-origin only — cross-origin responses
      // override via Content-Disposition, but the presigned URL here
      // does not set one, so this hint takes effect.
      a.download = buildExportFilename(optionsRef.current.projectNumber);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      toast.dismiss(toastIdRef.current);
      toast.success('Export downloaded successfully');
      setIsExporting(false);
      setJobId(null);
      lastProgressToastPct.current = -1;
    } else if (exportStatus.status === 'failed' || exportStatus.status === 'cancelled') {
      toast.dismiss(toastIdRef.current);
      toast.error(
        exportStatus.error_message ||
        (exportStatus.status === 'cancelled' ? 'Export cancelled' : 'Export failed'),
      );
      setIsExporting(false);
      setJobId(null);
      lastProgressToastPct.current = -1;
    } else if (exportStatus.status === 'processing') {
      const total = exportStatus.total_scenarios || 0;
      const processed = exportStatus.scenarios_processed || 0;
      const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
      // Only rewrite the toast when the displayed value changes — prevents
      // a 2s flicker loop where the toast is dismissed+recreated every poll.
      if (pct !== lastProgressToastPct.current) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = toast.loading(
          `Generating CSV export... ${total > 0 ? `${pct}%` : ''}`,
        );
        lastProgressToastPct.current = pct;
      }
    }
  }, [exportStatus, isExporting]);

  const startExport = useCallback(
    async (projectId: string, options: StartExportOptions = {}) => {
      try {
        optionsRef.current = options;
        setIsExporting(true);
        lastProgressToastPct.current = -1;
        toastIdRef.current = toast.loading('Starting CSV export...');
        const result = await reportsApi.startCsvExport(projectId, options.filters);
        setJobId(result.job_id);
      } catch (err) {
        toast.dismiss(toastIdRef.current);
        toast.error(
          err instanceof Error ? err.message : 'Failed to start export',
        );
        setIsExporting(false);
      }
    },
    [],
  );

  const cancelExport = useCallback(async () => {
    if (!jobId) return;
    try {
      await reportsApi.cancelCsvExport(jobId);
      // Status polling will observe the new state and clean up the toast.
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to cancel export');
    }
  }, [jobId]);

  return { isExporting, startExport, cancelExport, currentJobId: jobId };
}
